import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { InputLabel } from '@wajeez-web/components/ui';
import { DateFormType, DateTimeFormType, TextFormType } from '../Types';

import {
  FieldValues,
  useController,
  UseControllerProps,
  UseFormSetError,
} from 'react-hook-form';
import { DatePicker } from 'antd';
import moment from 'moment';
import dayjs from 'dayjs';
import {
  convertLocalDateTimeToEnd,
  convertLocalDateTimeToZero,
} from '@wajeez-web/core/utils';
export interface FormDateInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: DateFormType | DateTimeFormType;
  setError?: UseFormSetError<TFormFields>;
  showTime?: boolean;
  disabled?: boolean;
  shape?: 'circle' | 'squre';
  color?: string;
  bg?: 'primary' | 'secondary' | string;
  widthClassName?: string;
  showErrors?: boolean;
  errorMessages?: {
    required?: string;
  };
  allowClear?: boolean;
  controlled?: boolean;
  endOfDay?: boolean;
}

const FormDateInput = <TFormFields extends FieldValues>({
  fieldType,
  shape = 'squre',
  color = 'textPrimary',
  bg = 'white',
  showTime,
  disabled,
  widthClassName,
  showErrors = true,
  errorMessages = {
    required: 'This field is required',
  },
  allowClear,
  controlled = false,
  endOfDay = false,
  ...props
}: FormDateInputProps<TFormFields>) => {
  const { field, fieldState } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,
      ...props.rules,
    },
  });

  const onSelect = (date: dayjs.Dayjs | null) => {
    if (controlled) {
      let convertedDate: string | undefined;

      if (date) {
        if (endOfDay) {
          convertedDate = convertLocalDateTimeToEnd(date);
        } else {
          convertedDate = convertLocalDateTimeToZero(date);
        }
      }

      field.onChange(convertedDate ? convertedDate : null);
    } else {
      field.onChange(date ? date.toISOString() : null);
    }

    field.onBlur();

    if (
      date &&
      fieldType.fieldOptions.minDate &&
      date.isBefore(fieldType.fieldOptions.minDate)
    ) {
      props.setError?.(props.name, { type: 'min' }, { shouldFocus: true });
    }

    if (
      date &&
      fieldType.fieldOptions.maxDate &&
      date.isAfter(fieldType.fieldOptions.maxDate)
    ) {
      props.setError?.(props.name, { type: 'max' }, { shouldFocus: true });
    }
  };

  const { t } = useTranslation();

  switch (fieldState.error?.type) {
    case 'required':
      fieldState.error.message = errorMessages?.required;

      break;
    default:
      break;
  }

  const isError = fieldState.error;

  return (
    <div className={`${widthClassName}`}>
      {fieldType.label && (
        <InputLabel
          text={
            t(
              `inputs.labels.${fieldType.label}`,
              fieldType.label.toString() || ''
            ) as string
          }
          name={field.name}
          required={fieldType.required}
        />
      )}
      <DatePicker
        disabled={disabled}
        className={`w-full input focus:ring-primary p-2 px-2 align-middle  focus:ring-4 cursor-pointer  focus:ring-opacity-10 focus:ring-offset-0  ${
          shape === 'circle' ? 'rounded-full' : 'rounded-md'
        } bg-${bg} text-${color} focus:border-primary block w-full sm:text-sm ${
          isError ? 'border-red-500' : 'border-gray-300'
        } `}
        placeholder={
          t(
            `inputs.placeholders.${fieldType.placeHolder}`,
            fieldType.placeHolder || ''
          ) as string
        }
        // showTime={showTime}
        // name={name}
        onChange={onSelect}
        onOk={onSelect}
        value={
          field.value
            ? controlled
              ? dayjs(field.value).subtract(
                  dayjs(field.value).utcOffset(),
                  'minute'
                )
              : dayjs(field.value)
            : null
        }
        allowClear={allowClear}
      />
      {isError && showErrors && (
        <div className="mt-2">
          <p className="mb-0 text-sm text-red-500">{`${
            (t(`inputs.messages.${fieldState.error?.message}`),
            fieldState.error?.message)
          }`}</p>
        </div>
      )}
    </div>
  );
};

export default FormDateInput;
